import { Authentication } from '@leedpulse-monorepo/classes';
import Failure from '../../../core/Failure';
import HttpClient from '../../../core/HttpClient';

// ─── Step 1: Create a SetupIntent to securely collect card details ────────────

interface CreateSetupIntentPayload {
  company_id: string;
  /** Company API key for security validation */
  company_api_key: string;
}

interface CreateSetupIntentResponse {
  client_secret: string;
}

/**
 * Create a Stripe SetupIntent for the company's Stripe customer.
 * Returns the client_secret to use with stripe.confirmCardSetup() on the frontend.
 */
export async function createSetupIntent(
  payload: CreateSetupIntentPayload,
  auth: Authentication,
): Promise<CreateSetupIntentResponse> {
  try {
    const { company_api_key, ...body } = payload;
    const response = await HttpClient.post(
      '/leedflow/stripe/setup-intent/create',
      body,
      auth.token,
      false,
      {
        'x-api-key': company_api_key,
        'x-user-uid': auth.id,
      },
    );
    return response.data;
  } catch (e) {
    console.error('createSetupIntent error:', e);
    if (e instanceof TypeError && e.message === 'Failed to fetch') {
      throw new StripeSubscriptionFailure('Le serveur ne répond pas');
    } else if (e instanceof Error) {
      throw new StripeSubscriptionFailure(e.message);
    }
    return Promise.reject(e);
  }
}

// ─── Step 2: Create the subscription with the confirmed payment method ────────

interface CreateSubscriptionPayload {
  company_id: string;
  /** Company API key for security validation */
  company_api_key: string;
  plan_id: string;
  professional_number: string;
  payment_method_id: string;
  address?: {
    line1: string;
    line2: string | null;
    city: string;
    state: string;
    postal_code: string;
    country: string;
  };
  billing_name?: string;
}

interface CreateSubscriptionResponse {
  subscription_id: string;
  status: string;
}

/**
 * Create a Stripe subscription after the card has been confirmed via SetupIntent.
 * Each subscription item carries the professional_number in its metadata
 * so invoice lines read: product × number × price.
 */
export async function confirmSubscription(
  payload: CreateSubscriptionPayload,
  auth: Authentication,
): Promise<CreateSubscriptionResponse> {
  try {
    const { company_api_key, ...body } = payload;
    const response = await HttpClient.post(
      '/leedflow/stripe/subscription/create',
      body,
      auth.token,
      false,
      {
        'x-api-key': company_api_key,
        'x-user-uid': auth.id,
      },
    );
    return response.data;
  } catch (e) {
    console.error('confirmSubscription error:', e);
    if (e instanceof TypeError && e.message === 'Failed to fetch') {
      throw new StripeSubscriptionFailure('Le serveur ne répond pas');
    } else if (e instanceof Error) {
      throw new StripeSubscriptionFailure(e.message);
    }
    return Promise.reject(e);
  }
}

// ─── Failure class ────────────────────────────────────────────────────────────

export class StripeSubscriptionFailure extends Failure {
  constructor(message = 'Unknown error', code = 'unknown-error') {
    super(message, code);
  }
}
